import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    type TextInputKeyPressEventData,
    type NativeSyntheticEvent,
    ScrollView,
    TextInput,
    View,
    Text
} from "react-native";
import generateStylesheet from "./stylesheet";
import {
    NCoreUIKitTheme,
    PageContainer
} from "ncore-ui-kit";
import RESTServiceContext from "../../services/restAPI/context";

type CommandHistory = {
    isError: boolean;
    command: string;
    output: string;
};

const Terminal = () => {
    const theme = NCoreUIKitTheme.useContext();
    const {
        colors
    } = theme;
    const stylesheet = generateStylesheet(theme);

    const [
        history,
        setHistory
    ] = useState<CommandHistory[]>([]);

    const [
        currentCommand,
        setCurrentCommand
    ] = useState<string>("");

    const [
        selection,
        setSelection
    ] = useState<{ end: number; start: number } | undefined>(undefined);

    const scrollViewRef = useRef<ScrollView>(null);

    const inputRef = useRef<TextInput>(null);

    useEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    }, []);

    const handleKeyPress = (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (e.nativeEvent.key === "ArrowUp") {
            const lastCmd = history[history.length - 1]?.command;
            if (lastCmd) {
                setCurrentCommand(lastCmd);
                setTimeout(() => {
                    setSelection({
                        start: lastCmd.length,
                        end: lastCmd.length
                    });
                }, 0);
            }
        }
    };

    const handleExecute = async (overrideCmd?: string) => {
        const cmdToRun = typeof overrideCmd === "string" ? overrideCmd : currentCommand;

        if(!cmdToRun.trim()) {
            return;
        }

        const cmd = cmdToRun;

        if (cmd.trim().toLowerCase() === "clear") {
            setHistory([]);
            setCurrentCommand("");
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return;
        }

        setCurrentCommand("");

        const cleanOutput = (text: string) => {
            if (!text) return "";
            // eslint-disable-next-line no-control-regex
            return text.replace(/\x1B\[[0-9;]*[a-zA-Z]/g, "").slice(-15000);
        };

        try {
            const response = await RESTServiceContext.action("executeCommand", {
                command: cmd
            });

            if(response.code === 200 && response.payload) {
                const out = cleanOutput(response.payload.stdout || response.payload.stderr || "success");
                setHistory(prev => [
                    ...prev,
                    {
                        isError: false,
                        command: cmd,
                        output: out
                    }
                ]);
            } else {
                setHistory(prev => [
                    ...prev,
                    {
                        output: cleanOutput(response.payload?.stderr || response.message || "error"),
                        isError: true,
                        command: cmd
                    }
                ]);
            }
        } catch (e: unknown) {
            const error = e as Error;
            setHistory(prev => [
                ...prev,
                {
                    output: cleanOutput(error.message || "Execution failed"),
                    isError: true,
                    command: cmd
                }
            ]);
        } finally {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    };

    return <PageContainer
        isCustomPadding={false}
        isScrollable={false}
    >
        <View
            onTouchEnd={() => {
                if (typeof window !== "undefined" && window.getSelection()?.toString()) {
                    return;
                }
                inputRef.current?.focus();
            }}
            {...({
                onClick: () => {
                    if (typeof window !== "undefined" && window.getSelection()?.toString()) {
                        return;
                    }
                    inputRef.current?.focus();
                }
            } as Record<string, unknown>)}
            style={{
                flex: 1
            }}
        >
            <View
                style={[
                    stylesheet.terminalContainer,
                    {
                        backgroundColor: colors.project.black || "#000000"
                    }
                ]}
            >
                <View
                    style={stylesheet.toolbarContainer}
                >
                    <View
                        onTouchEnd={() => handleExecute("pm2 logs --nostream")}
                        {...({
                            onClick: () => handleExecute("pm2 logs --nostream")
                        } as Record<string, unknown>)}
                        style={stylesheet.toolbarButton}
                    >
                        <Text
                            style={stylesheet.toolbarButtonText}
                        >
                            Logları Getir
                        </Text>
                    </View>
                    <View
                        onTouchEnd={() => handleExecute("df -h")}
                        {...({
                            onClick: () => handleExecute("df -h")
                        } as Record<string, unknown>)}
                        style={stylesheet.toolbarButton}
                    >
                        <Text
                            style={stylesheet.toolbarButtonText}
                        >
                            Disk Durumu
                        </Text>
                    </View>
                    <View
                        onTouchEnd={() => handleExecute("free -h")}
                        {...({
                            onClick: () => handleExecute("free -h")
                        } as Record<string, unknown>)}
                        style={stylesheet.toolbarButton}
                    >
                        <Text
                            style={stylesheet.toolbarButtonText}
                        >
                            RAM Durumu
                        </Text>
                    </View>
                    <View
                        onTouchEnd={() => handleExecute("ls")}
                        {...({
                            onClick: () => handleExecute("ls")
                        } as Record<string, unknown>)}
                        style={stylesheet.toolbarButton}
                    >
                        <Text
                            style={stylesheet.toolbarButtonText}
                        >
                            Dosyaları Listele
                        </Text>
                    </View>
                    <View
                        onTouchEnd={() => handleExecute("top -b -n 1")}
                        {...({
                            onClick: () => handleExecute("top -b -n 1")
                        } as Record<string, unknown>)}
                        style={stylesheet.toolbarButton}
                    >
                        <Text
                            style={stylesheet.toolbarButtonText}
                        >
                            Sistem Kaynakları
                        </Text>
                    </View>
                    <View
                        {...({
                            onClick: () => {
                                const cmd = "node /root/projects/scripts/update-service.js --name ";
                                setCurrentCommand(cmd);
                                setTimeout(() => {
                                    setSelection({
                                        start: cmd.length,
                                        end: cmd.length
                                    });
                                    inputRef.current?.focus();
                                }, 0);
                            }
                        } as Record<string, unknown>)}
                        onTouchEnd={() => {
                            const cmd = "node /root/projects/scripts/update-service.js --name ";
                            setCurrentCommand(cmd);
                            setTimeout(() => {
                                setSelection({
                                    start: cmd.length,
                                    end: cmd.length
                                });
                                inputRef.current?.focus();
                            }, 0);
                        }}
                        style={stylesheet.toolbarButton}
                    >
                        <Text
                            style={stylesheet.toolbarButtonText}
                        >
                            Servis Güncelle
                        </Text>
                    </View>
                </View>
                <ScrollView
                    onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({
                        animated: true
                    })}
                    style={stylesheet.outputContainer}
                    ref={scrollViewRef}
                >
                    {
                        history.map((h, index) => <View
                            key={index}
                        >
                            <Text
                                style={stylesheet.promptText}
                                selectable={true}
                            >
                                root@gmcore:~$ {h.command}
                            </Text>
                            <Text
                                style={h.isError ? stylesheet.errorText : stylesheet.outputText}
                                selectable={true}
                            >
                                {h.output}
                            </Text>
                        </View>)
                    }
                    <View
                        style={stylesheet.inputContainer}
                    >
                        <Text
                            style={stylesheet.promptText}
                            selectable={true}
                        >
                            root@gmcore:~$
                        </Text>
                        <TextInput
                            onSelectionChange={e => setSelection(e.nativeEvent.selection)}
                            onSubmitEditing={() => handleExecute()}
                            onChangeText={setCurrentCommand}
                            style={stylesheet.textInput}
                            onKeyPress={handleKeyPress}
                            value={currentCommand}
                            selection={selection}
                            autoCapitalize="none"
                            autoCorrect={false}
                            autoFocus={true}
                            ref={inputRef}
                        />
                    </View>
                </ScrollView>
            </View>
        </View>
    </PageContainer>;
};

export default Terminal;
